import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom'
import Grid from '@material-ui/core/Grid';

import { makeStyles } from "@material-ui/core/styles";
import FilterListIcon from '@material-ui/icons/FilterList';
import TextField from '@material-ui/core/TextField';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import InputAdornment from '@material-ui/core/InputAdornment';
import SearchIcon from '@material-ui/icons/Search';
import Collapse from '@material-ui/core/Collapse';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import EditIcon from '@material-ui/icons/Edit';
import LockIcon from '@material-ui/icons/Lock';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
import Tooltip from '@material-ui/core/Tooltip';

import './CardList.css';
import { getImageUrl, hasValidImage } from './utils';
import Pagination from './Pagination';


const useStyles = makeStyles(theme => ({
    webPlant: {
        padding: '7px 0px 0px 20px',
        display: 'inline-flex',
        [theme.breakpoints.down('xs')]: {
            padding: '7px 8px 0px 8px'
        }
    },
    heroesContainer: {
        marginLeft: '16px',
        [theme.breakpoints.down('xs')]: {
            marginLeft: '8px'
        }
    },
    heroGridContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'flex-start',
        [theme.breakpoints.down('xs')]: {
            justifyContent: 'center'
        }
    },
    heroCard: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '168px',
        textAlign: 'center'
    },
    heroImage: {
        width: '168px',
        height: '252px',
        objectFit: 'cover',
        display: 'block'
    },
    heroPlaceholder: {
        width: '168px',
        minWidth: '168px',
        height: '252px',
        minHeight: '252px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f0f0f0',
        border: '1px solid #ccc',
        textAlign: 'center',
        padding: '12px',
        boxSizing: 'border-box',
        fontSize: '1em',
        wordWrap: 'break-word',
        overflow: 'hidden'
    },
    heroTitle: {
        width: '168px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        fontSize: '0.9em',
        marginTop: '4px'
    },
    filterButton: {
        backgroundColor: theme.palette.background.paper,
        border: '1px solid',
        borderColor: theme.palette.divider,
        '&:hover': {
            backgroundColor: theme.palette.action.hover
        }
    },
    filterContainer: {
        padding: '12px 16px',
        marginBottom: '12px',
        backgroundColor: theme.palette.background.paper,
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    },
    filterInput: {
        width: '100%',
        maxWidth: '300px'
    },
    heroCardWrapper: {
        position: 'relative'
    },
    editButton: {
        position: 'absolute',
        top: '4px',
        right: '4px',
        backgroundColor: 'rgba(255,255,255,0.9)',
        padding: '4px',
        '&:hover': {
            backgroundColor: 'rgba(255,255,255,1)'
        }
    },
    lockBadge: {
        position: 'absolute',
        top: '4px',
        left: '4px',
        color: '#f57c00',
        pointerEvents: 'none'
    },
    dialogTitle: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    customImageField: {
        width: '100%',
        marginBottom: theme.spacing(2)
    },
    heroPreviewImage: {
        width: '168px',
        height: '252px',
        objectFit: 'cover',
        display: 'block',
        margin: '0 auto'
    }
}));


const Hero = (props) => {
    const classes = useStyles();

    // Extract only the card data, excluding functions
    const cardData = {
        id: props.id,
        name: props.name,
        description: props.description,
        thumbnail: props.thumbnail,
        publisher: props.publisher || 'Unknown',
        source: props.source,
        comicVineId: props.comicVineId,
        comicVineImage: props.comicVineImage,
        locked: props.locked || false,
        resourceType: props.resourceType,
        // Legacy Marvel fields
        resourceURI: props.resourceURI,
        series: props.series,
        stories: props.stories,
        urls: props.urls,
        createDate: props.createDate,
        modified: props.modified
    };

    const hasImage = hasValidImage(props);
    const [editDialogOpen, setEditDialogOpen] = React.useState(false);
    const [customImageUrl, setCustomImageUrl] = React.useState('');
    const [editedName, setEditedName] = React.useState('');
    const [editedDescription, setEditedDescription] = React.useState('');
    const [editedPublisher, setEditedPublisher] = React.useState('');
    const [editedResourceType, setEditedResourceType] = React.useState('');
    const [editedLocked, setEditedLocked] = React.useState(false);

    const handleEditClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        // Initialize form fields with current values
        setCustomImageUrl('');
        setEditedName(props.name || '');
        setEditedDescription(props.description || '');
        setEditedPublisher(props.publisher || '');
        setEditedResourceType(props.resourceType || 'character');
        setEditedLocked(props.locked || false);
        setEditDialogOpen(true);
    };

    const handleSave = () => {
        const updates = {
            name: props.name, // Name shouldn't change as it's the key
            customImageUrl: customImageUrl.trim() || null,
            description: editedDescription.trim() || props.description,
            publisher: editedPublisher.trim() || props.publisher,
            resourceType: editedResourceType || props.resourceType,
            locked: editedLocked
        };
        
        if (props.updateHero) {
            props.updateHero(props.name, updates);
        }
        
        setEditDialogOpen(false);
        setCustomImageUrl('');
    };

    return (
        <Grid item className={classes.webPlant}>
            <div className={classes.heroCardWrapper}>
                <Link to={props.editMode ? "/" : "/comicseries/"} onClick={() => props.setCurrentCard(cardData)}>
                    <div className={classes.heroCard}>
                        {hasImage ? (
                            <img 
                                className={classes.heroImage}
                                src={getImageUrl(props, 'portrait_fantastic')} 
                                alt={props.name} 
                                title={props.name} 
                            />
                        ) : (
                            <div className={classes.heroPlaceholder}>
                                {props.name}
                            </div>
                        )}
                        <div className={classes.heroTitle}>{props.name}</div>
                    </div>
                </Link>
                {props.locked && (
                    <Tooltip title="Locked: Comic Vine lookup skipped">
                        <span className={classes.lockBadge}>
                            <LockIcon fontSize="small" />
                        </span>
                    </Tooltip>
                )}
                {props.updateHeroImage && (
                    <IconButton 
                        className={classes.editButton}
                        size="small"
                        onClick={handleEditClick}
                        title="Edit hero image"
                    >
                        <EditIcon fontSize="small" />
                    </IconButton>
                )}
            </div>
            
            {/* Edit Hero Dialog */}
            <Dialog 
                open={editDialogOpen} 
                onClose={() => setEditDialogOpen(false)}
                fullWidth
                maxWidth="sm"
                PaperProps={{
                    style: {
                        position: 'fixed',
                        top: '10%',
                        margin: 0
                    }
                }}
            >
                <DialogTitle className={classes.dialogTitle}>
                    <span>Edit Hero: {props.name}</span>
                    <IconButton size="small" onClick={() => setEditDialogOpen(false)}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    {hasImage && (
                        <img 
                            className={classes.heroPreviewImage}
                            src={getImageUrl(props, 'portrait_fantastic')} 
                            alt={props.name} 
                        />
                    )}
                    
                    <TextField
                        className={classes.customImageField}
                        variant="outlined"
                        size="small"
                        fullWidth
                        label="Custom Image URL"
                        placeholder="Enter image URL..."
                        value={customImageUrl}
                        onChange={(e) => setCustomImageUrl(e.target.value)}
                        style={{ marginTop: '16px', marginBottom: '16px' }}
                    />
                    
                    <TextField
                        variant="outlined"
                        size="small"
                        fullWidth
                        label="Name"
                        value={editedName}
                        onChange={(e) => setEditedName(e.target.value)}
                        disabled
                        style={{ marginBottom: '16px' }}
                        helperText="Name cannot be changed (it's used as the unique identifier)"
                    />
                    
                    <TextField
                        variant="outlined"
                        size="small"
                        fullWidth
                        multiline
                        rows={3}
                        label="Description"
                        value={editedDescription}
                        onChange={(e) => setEditedDescription(e.target.value)}
                        style={{ marginBottom: '16px' }}
                    />
                    
                    <TextField
                        variant="outlined"
                        size="small"
                        fullWidth
                        label="Publisher"
                        value={editedPublisher}
                        onChange={(e) => setEditedPublisher(e.target.value)}
                        style={{ marginBottom: '16px' }}
                    />
                    
                    <FormControl fullWidth variant="outlined" size="small" style={{ marginBottom: '16px' }}>
                        <InputLabel id="resource-type-edit-label">Resource Type</InputLabel>
                        <Select
                            labelId="resource-type-edit-label"
                            value={editedResourceType}
                            onChange={(e) => setEditedResourceType(e.target.value)}
                            label="Resource Type"
                        >
                            <MenuItem value="character">Character</MenuItem>
                            <MenuItem value="team">Team</MenuItem>
                            <MenuItem value="storyarc">Story Arc</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControlLabel
                        control={
                            <Switch
                                checked={editedLocked}
                                onChange={(e) => setEditedLocked(e.target.checked)}
                                color="primary"
                            />
                        }
                        label={editedLocked ? 'Locked (Comic Vine lookup skipped)' : 'Unlocked (Comic Vine lookup enabled)'}
                        style={{ marginBottom: '8px' }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditDialogOpen(false)}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleSave} 
                        color="primary" 
                        variant="contained"
                    >
                        Save Changes
                    </Button>
                </DialogActions>
            </Dialog>
        </Grid>
    );
};

const Heroes = (props) => {
    const classes = useStyles();
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    
    // Filter state
    const [filterOpen, setFilterOpen] = useState(false);
    const [filterText, setFilterText] = useState('');
    
    // Filter heroes by text
    const filteredCards = useMemo(() => {
        if (!filterText.trim()) return props.cards;
        
        const searchLower = filterText.toLowerCase();
        return props.cards.filter(hero => 
            hero.name?.toLowerCase().includes(searchLower)
        );
    }, [props.cards, filterText]);
    
    // Apply pagination
    const paginatedCards = useMemo(() => {
        const startIndex = (currentPage - 1) * pageSize;
        return filteredCards.slice(startIndex, startIndex + pageSize);
    }, [filteredCards, currentPage, pageSize]);
    
    // Reset pagination when cards list changes or filter changes
    React.useEffect(() => {
        setCurrentPage(1);
    }, [props.cards.length, filterText]);
    
    // Shared pagination props to avoid duplication
    const paginationProps = {
        currentPage,
        totalItems: filteredCards.length,
        pageSize,
        onPageChange: setCurrentPage,
        onPageSizeChange: setPageSize
    };
    
    // Filter button to pass to pagination
    const filterButtonElement = (
        <IconButton 
            className={classes.filterButton}
            onClick={() => setFilterOpen(!filterOpen)}
            aria-label="Filter"
            size="small"
        >
            <FilterListIcon />
        </IconButton>
    );
    
    return (
        <div className={classes.heroesContainer}>
            {props.cards.length > 0 && (
                <Pagination {...paginationProps} filterButton={filterButtonElement} />
            )}
            
            {/* Filter collapse section */}
            <Collapse in={filterOpen}>
                <div className={classes.filterContainer}>
                    <TextField
                        className={classes.filterInput}
                        variant="outlined"
                        size="small"
                        placeholder="Filter heroes..."
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon color="action" />
                                </InputAdornment>
                            ),
                            endAdornment: filterText && (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setFilterText('')}>
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />
                </div>
            </Collapse>
            
            <div className={classes.heroGridContainer}>
                {paginatedCards.map(card => <Hero key={card.id} {...card} setCurrentCard={props.setCurrentCard} editMode={props.editMode} updateHeroImage={props.updateHeroImage} updateHero={props.updateHero} />)}
            </div>
            
            {props.cards.length > pageSize && (
                <Pagination {...paginationProps} />
            )}
        </div>
    )
}

export default Heroes;